import type {
  BugDetail,
  BugReviewBundle,
  ChatMessage,
  JiraTransition,
} from '@sr-test-wizard/shared';
import { api } from './api';

/**
 * Bug review API client. Mirrors the Phase 1 (TEXEC) / Phase 2 (Env Links) pattern: the
 * list endpoint returns lean items, the detail endpoint returns the full
 * comment + attachment payload on demand. Writes (`comment`, `transitions`,
 * `repull-source`) trigger server-side WS broadcasts that the client store
 * patches in.
 */
export const bugReviewApi = {
  list: (runId: string) =>
    api.get<BugReviewBundle>(`/api/runs/${runId}/bugs`),
  refresh: (runId: string) =>
    api.post<BugReviewBundle>(`/api/runs/${runId}/bugs/refresh`),
  detail: (runId: string, bugKey: string) =>
    api.get<BugDetail>(`/api/runs/${runId}/bugs/${bugKey}`),
  listChat: (runId: string, bugKey: string) =>
    api.get<ChatMessage[]>(`/api/runs/${runId}/bugs/${bugKey}/chat`),
  sendChat: (runId: string, bugKey: string, question: string) =>
    api.post<{ reply: string; message: ChatMessage }>(
      `/api/runs/${runId}/bugs/${bugKey}/chat`,
      { question },
    ),
  draftComment: (runId: string, bugKey: string, intent?: string) =>
    api.post<{ bodyMd: string }>(
      `/api/runs/${runId}/bugs/${bugKey}/comment/draft`,
      intent ? { intent } : {},
    ),
  postComment: (runId: string, bugKey: string, bodyMd: string) =>
    api.post<BugDetail>(`/api/runs/${runId}/bugs/${bugKey}/comment`, {
      bodyMd,
    }),
  listTransitions: (runId: string, bugKey: string) =>
    api.get<JiraTransition[]>(`/api/runs/${runId}/bugs/${bugKey}/transitions`),
  transition: (
    runId: string,
    bugKey: string,
    transitionId: string,
    bodyMd?: string,
  ) =>
    api.post<BugDetail>(`/api/runs/${runId}/bugs/${bugKey}/transitions`, {
      transitionId,
      bodyMd,
    }),
  repullSource: (runId: string, bugKey: string) =>
    api.post<{ projectPath: string; orderKey: string; bytes: number }>(
      `/api/runs/${runId}/bugs/${bugKey}/repull-source`,
    ),
  uploadImage: async (runId: string, file: File): Promise<{ name: string; size: number; url: string }> => {
    const url = `/api/runs/${runId}/images/upload?name=${encodeURIComponent(file.name)}`;
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
      },
      body: file,
    });
    const json = await res.json().catch(() => ({ success: false, error: 'Invalid JSON' }));
    if (!res.ok || !json.success) {
      throw new Error(json.error || `Upload failed: ${res.status}`);
    }
    return json.data;
  },
};
